import axios, { AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import { tokenStorage } from '@/lib/storage';
import { ApiError, type ApiErrorEnvelope } from '@/api/errors';

const API_BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

/** Requests that must never carry a stale Bearer token / trigger the refresh flow. */
const PUBLIC_PATHS = ['/auth/login', '/auth/refresh'];

/**
 * Extended Axios request config used to mark a request as already retried once (after a token
 * refresh) so we never loop refreshing forever.
 */
interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

/**
 * Invoked when the refresh flow itself fails (refresh token invalid/expired/reused) — the
 * AuthProvider subscribes to this to force a client-side logout + redirect to /login without
 * this module needing to know about React/router.
 */
type AuthFailureHandler = () => void;
let authFailureHandler: AuthFailureHandler | null = null;

export function registerAuthFailureHandler(handler: AuthFailureHandler): void {
  authFailureHandler = handler;
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const isPublic = PUBLIC_PATHS.some((p) => config.url?.includes(p));
  if (!isPublic) {
    const token = tokenStorage.getAccessToken();
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
  }
  return config;
});

// --- Refresh-on-401 flow ----------------------------------------------------------------
// Concurrent 401s while a refresh is already in flight are queued on `refreshPromise` so we
// only ever call POST /auth/refresh once per expiry, then replay every queued request with the
// new access token (mirrors 07-cross-cutting.md §2 rotation: refresh tokens are single-use).
let refreshPromise: Promise<string> | null = null;

async function performRefresh(): Promise<string> {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  // Deliberately bypasses `apiClient`'s interceptors (plain axios call) to avoid recursion.
  const response = await axios.post<{ accessToken: string; refreshToken: string }>(
    `${API_BASE_URL}/auth/refresh`,
    { refreshToken },
  );

  tokenStorage.setTokens(response.data.accessToken, response.data.refreshToken);
  return response.data.accessToken;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorEnvelope>) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined;
    const isPublic = PUBLIC_PATHS.some((p) => originalRequest?.url?.includes(p));

    const isUnauthorized = error.response?.status === 401;

    if (isUnauthorized && !isPublic && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        refreshPromise ??= performRefresh().finally(() => {
          refreshPromise = null;
        });
        const newAccessToken = await refreshPromise;

        originalRequest.headers.set('Authorization', `Bearer ${newAccessToken}`);
        return apiClient.request(originalRequest as AxiosRequestConfig);
      } catch (refreshError) {
        tokenStorage.clear();
        authFailureHandler?.();
        return Promise.reject(ApiError.fromUnknown(refreshError));
      }
    }

    return Promise.reject(ApiError.fromUnknown(error));
  },
);
